// The marketing site and the LMS are one Vite app served from two hostnames.
// Keep every link between them explicit so a marketing visitor never sees LMS
// chrome, and an LMS visitor never falls back into the marketing site.

export type AppSurface = 'marketing' | 'lms';

const DEFAULT_MARKETING_URL = 'https://lsem.org.ng';
const DEFAULT_LMS_URL = 'https://lms.lsem.org.ng';

const trimSlash = (value: string) => value.replace(/\/+$/, '');

const isLocalHost = (hostname: string) =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname.endsWith('.localhost') ||
  hostname.endsWith('.127.0.0.1');

function localBaseUrl(surface: AppSurface): string | null {
  const { protocol, hostname, port } = window.location;
  if (!isLocalHost(hostname)) return null;

  const baseHost = hostname
    .replace(/^lms\./, '')
    .replace(/^www\./, '')
    .replace(/^127\.0\.0\.1$/, 'localhost')
    .replace(/\.127\.0\.0\.1$/, '.localhost') || 'localhost';
  const host = surface === 'lms' ? `lms.${baseHost}` : baseHost;
  return `${protocol}//${host}${port ? `:${port}` : ''}`;
}

function withPath(base: string, path: string): string {
  const cleanBase = trimSlash(base);
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
}

/**
 * Marketing host is `lsem.org.ng`; LMS host is `lms.lsem.org.ng`.
 * Local development can use `?surface=lms` once (the choice is kept in
 * sessionStorage) or open the app at `http://lms.localhost:5173`.
 */
export function getSurface(): AppSurface {
  if (import.meta.env.VITE_FORCE_SURFACE === 'lms') return 'lms';
  if (import.meta.env.VITE_FORCE_SURFACE === 'marketing') return 'marketing';

  if (!import.meta.env.PROD && typeof window !== 'undefined') {
    const requested = new URLSearchParams(window.location.search).get('surface');
    if (requested === 'lms' || requested === 'marketing') {
      window.sessionStorage.setItem('lsem-surface', requested);
    }
    const remembered = window.sessionStorage.getItem('lsem-surface');
    if (remembered === 'lms' || remembered === 'marketing') return remembered;
  }

  const hostname = window.location.hostname.toLowerCase();
  return hostname.startsWith('lms.') ? 'lms' : 'marketing';
}

export const isLmsSurface = () => getSurface() === 'lms';

export function marketingHref(path = '/'): string {
  if (getSurface() === 'marketing') return path;
  const base = localBaseUrl('marketing') || trimSlash(import.meta.env.VITE_MARKETING_URL || DEFAULT_MARKETING_URL);
  return withPath(base, path);
}

export function lmsHref(path = '/'): string {
  if (getSurface() === 'lms') return path;
  const base = localBaseUrl('lms') || trimSlash(import.meta.env.VITE_LMS_URL || DEFAULT_LMS_URL);
  return withPath(base, path);
}

/**
 * Absolute LMS URL for outbound auth callbacks. `lmsHref` stays relative on the
 * LMS surface for in-app routing, but Supabase email redirects must always be
 * absolute so local/preview confirmation links return to the host that sent
 * them instead of the production Site URL.
 */
export function lmsUrl(path = '/'): string {
  const local = localBaseUrl('lms');
  const base = local || trimSlash(import.meta.env.VITE_LMS_URL || DEFAULT_LMS_URL);
  return withPath(base, path);
}

/** Current URL path plus query/hash, for legacy-route handoffs. */
export function currentPathWithSearch(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}
